/**
 * SepaMandateBlock — Bloc de mise en place du mandat SEPA après signature RCD.
 *
 * S'affiche dans StepConfirmation après la souscription. Calcule les 40% restants,
 * propose le bouton "Mettre en place le prélèvement SEPA" qui redirige vers Stripe (mandat SEPA).
 *
 * Props :
 *   souscriptionId : string (identifiant de la souscription créée)
 *   montantTotalTTC : number (montant TTC global)
 *   formData : { email, prenom_gerant, nom_gerant, raison_sociale, ... }
 */
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Banknote, Calendar, ShieldCheck, Loader2, CheckCircle2, AlertCircle, Settings2, Lock } from 'lucide-react';
import OscopLogo from '../../components/brand/OscopLogo';
import { useAuth } from '../../contexts/AuthContext';
import SepaPreSignCustomizeModal from './SepaPreSignCustomizeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtEUR(cents) {
  return ((cents || 0) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

const STATUS_BADGE = {
  scheduled: { label: 'Planifiée', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  pending: { label: 'En cours', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  submitted: { label: 'Soumise', cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  paid: { label: 'Payée', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { label: 'Échouée', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  cancelled: { label: 'Annulée', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  chargedback: { label: 'Contestée', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export default function SepaMandateBlock({ souscriptionId, montantTotalTTC, formData, referenceSolidaire, onSplitChange, echelonnementOverride = null, acompteVirementConfirme = true }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth() || {};
  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';

  // V35.41 — Overrides admin pour personnaliser l'échéancier AVANT signature mandat
  const [customOpen, setCustomOpen] = useState(false);
  // V36.74 — Pré-remplit depuis l'échéancier persisté sur la proposition.
  // Le Super Admin l'a déjà configuré dans le simulateur ; pas besoin de
  // refaire la saisie ici.
  const [adminOverrides, setAdminOverrides] = useState(() => {
    if (echelonnementOverride && (echelonnementOverride.nb_echeances || echelonnementOverride.offsets_days)) {
      return {
        nb_echeances: echelonnementOverride.nb_echeances || 3,
        offsets_days: echelonnementOverride.offsets_days || [30, 60, 90],
        reference: null,
      };
    }
    return null;
  });
  // V35.42 — Personnalisation du split acompte/SEPA (défaut 60/40)
  // V36.74 — Pré-remplit le split depuis acompte_pct de l'override.
  const [splitOverride, setSplitOverride] = useState(() => {
    if (echelonnementOverride && echelonnementOverride.acompte_pct != null) {
      const acompte = Number(echelonnementOverride.acompte_pct);
      const sepaPct = Math.max(0, 1 - acompte);
      const sepaCents = Math.round((montantTotalTTC || 0) * sepaPct * 100);
      return { sepa_pct: sepaPct, montant_sepa_cents: sepaCents };
    }
    return null;
  });

  // Échéancier du devis : dates et montants fixés par le Super Admin (prioritaires)
  const nbDevis = Number(echelonnementOverride?.nb_echeances) || 0;
  const montantsDevisCents = (echelonnementOverride?.montants_eur || []).length && nbDevis
    ? echelonnementOverride.montants_eur.slice(0, nbDevis).map((v) => Math.round(Number(v) * 100))
    : null;
  const datesDevis = (echelonnementOverride?.dates_iso || []).some(Boolean) ? echelonnementOverride.dates_iso : null;

  // Calcul du montant SEPA en centimes (40% par défaut, modifiable par super_admin)
  const defaultSepaCents = Math.round((montantTotalTTC || 0) * 0.4 * 100);
  const montant40Cents = montantsDevisCents ? montantsDevisCents.reduce((a, b) => a + b, 0) : (splitOverride?.montant_sepa_cents ?? defaultSepaCents);

  const pctSepa = montantTotalTTC ? Math.round((montant40Cents / (montantTotalTTC * 100)) * 100) : 40;
  const pctAcompte = 100 - pctSepa;

  // Notifier le parent (StepPaiement) du split actif pour qu'il affiche le bon montant à virer
  useEffect(() => {
    if (typeof onSplitChange === 'function') {
      const sepaCents = montant40Cents;
      const acompteCents = Math.round((montantTotalTTC || 0) * 100) - sepaCents;
      onSplitChange(acompteCents / 100, sepaCents / 100);
    }
  }, [montant40Cents, montantTotalTTC, onSplitChange]);

  // Référence RCDS officielle (affichée + envoyée à GoCardless).
  // V36.5 — Si formData contient un numéro de proposition (devisId/numero_simulation),
  // on l'ajoute en suffixe pour traçabilité côté GoCardless / Qonto :
  //   "RCDS-SOL-12345 / PROP-SIM-0F6BB504"
  const baseRef = referenceSolidaire ? `RCDS-${referenceSolidaire}` : `RCDS-${souscriptionId?.slice(0, 14) || ''}`;
  const propRef = formData?.devis_id || formData?.numero_simulation || formData?.proposition_id || null;
  const refRCDS = propRef ? `${baseRef} / PROP-${propRef}` : baseRef;

  // Récupère l'état du plan SEPA s'il existe
  useEffect(() => {
    if (!souscriptionId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/sepa-stripe/plan/${souscriptionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPlan(res.data);
      } catch (e) {
        // 404 = pas encore de plan → on affiche le bouton d'initiation
        if (e.response?.status !== 404) {
          console.warn('SEPA plan fetch error', e);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [souscriptionId]);

  const handleInitMandate = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        souscription_id: souscriptionId,
        montant_total_cents: montant40Cents,
        customer_email: formData?.email || '',
        customer_given_name: formData?.prenom_gerant || formData?.prenom || 'Client',
        customer_family_name: formData?.nom_gerant || formData?.nom || formData?.raison_sociale || 'RCD',
        description: `RCDS Solidaire - ${pctSepa}% en ${adminOverrides?.nb_echeances || 3} prélèvements - ${refRCDS}`,
        nb_echeances: adminOverrides?.nb_echeances || 3,
        offsets_days: adminOverrides?.offsets_days || [30, 60, 90],
        reference: adminOverrides?.reference || refRCDS,
        ...(datesDevis && { dates_iso: datesDevis }),
        ...(montantsDevisCents && { amounts_cents: montantsDevisCents }),
      };
      if (adminOverrides?.amounts_cents) {
        // Validation côté backend : amounts_cents doit avoir nb_echeances éléments
        // et sommer à montant_total_cents (cf. admin_customize_schedule).
        // Ici on ne l'envoie pas à /init car la route /init répartit automatiquement.
        // Mais on l'utilisera via PUT /admin/plans/{id}/customize APRÈS init si fourni.
      }
      const res = await axios.post(`${API}/sepa-stripe/redirect-flow/init`, payload, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      // Persister l'ID local pour la page de retour
      try {
        localStorage.setItem('sepa_pending', JSON.stringify({
          souscription_id: souscriptionId,
          sepa_plan_id: res.data.sepa_plan_id,
        }));
      } catch { /* noop */ }
      window.location.href = res.data.redirect_url;
    } catch (e) {
      console.error('SEPA init error', e);
      toast.error(e.response?.data?.detail || 'Impossible d\'initier le mandat SEPA');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-5 rounded-xl bg-white border border-slate-200 flex items-center justify-center" data-testid="sepa-loading">
        <Loader2 className="h-5 w-5 animate-spin text-[#DEA900]" />
      </div>
    );
  }

  // Calculs pour l'affichage — basés sur overrides admin si présents
  const nbEcheancesPreview = adminOverrides?.nb_echeances || 3;
  const offsetsPreview = adminOverrides?.offsets_days || [30, 60, 90];
  const baseCents = Math.floor(montant40Cents / nbEcheancesPreview);
  const restCents = montant40Cents - baseCents * nbEcheancesPreview;
  const previewAmounts = montantsDevisCents || (adminOverrides?.amounts_cents
    ? adminOverrides.amounts_cents
    : Array.from({ length: nbEcheancesPreview }, (_, i) => baseCents + (i === nbEcheancesPreview - 1 ? restCents : 0)));
  const previewDates = Array.from({ length: nbEcheancesPreview }, (_, i) => {
    if (datesDevis && datesDevis[i]) return datesDevis[i];
    const dt = new Date();
    dt.setDate(dt.getDate() + (offsetsPreview[i] ?? (i + 1) * 30));
    return dt.toISOString().slice(0, 10);
  });

  // === CAS 1 : Pas encore de plan → CTA initialisation ===
  if (!plan) {
    return (
      <div className="relative overflow-hidden rounded-2xl border border-[#DEA900]/30 bg-white" data-testid="sepa-init-block">
        {/* Bandeau navy O'SCOP */}
        <div className="relative bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] p-5 text-white">
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")" }} />
          <div className="relative flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#DEA900] flex items-center justify-center shadow-md shadow-[#DEA900]/30 ring-1 ring-[#f5cc3e]/40 shrink-0">
              <OscopLogo size={28} variant="on-navy" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg leading-none">Étape 2 — Prélèvement SEPA Stripe ({pctSepa} % restants)</h3>
              <p className="text-[#f5cc3e] text-[11px] uppercase tracking-wider mt-1.5">
                Mandat SEPA sécurisé via Stripe · {nbEcheancesPreview} échéance{nbEcheancesPreview > 1 ? 's' : ''} mensuelle{nbEcheancesPreview > 1 ? 's' : ''} · {refRCDS}
                {adminOverrides && <span className="ml-2 text-emerald-300">· ✱ Personnalisé</span>}
              </p>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Récap */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Montant total à prélever</p>
              <p className="text-2xl font-bold text-[#0a3d62] tabular-nums mt-1">{fmtEUR(montant40Cents)}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{pctSepa} % de {((montantTotalTTC || 0).toLocaleString('fr-FR'))} €</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nombre d'échéances</p>
              <p className="text-2xl font-bold text-[#b88600] tabular-nums mt-1">{nbEcheancesPreview} mensualité{nbEcheancesPreview > 1 ? 's' : ''}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">~{fmtEUR(baseCents)} / mois</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Première échéance</p>
              <p className="text-2xl font-bold text-emerald-700 tabular-nums mt-1">{datesDevis?.[0] ? fmtDate(previewDates[0]) : `J + ${offsetsPreview[0]} jours`}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{datesDevis?.[0] ? 'date fixée sur le devis' : 'après signature mandat'}</p>
            </div>
          </div>

          {/* Calendrier prévisionnel */}
          <div className="bg-gradient-to-br from-[#0a3d62] to-[#0c4a78] border border-[#0a3d62] rounded-lg p-4" data-testid="sepa-calendar">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold text-white uppercase tracking-wide flex items-center gap-2 whitespace-nowrap">
                <Calendar className="h-4 w-4 text-[#f5cc3e] shrink-0" />
                <span>Calendrier prévisionnel</span>
              </p>
              {isSuperAdmin && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setCustomOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-[11px] bg-[#DEA900] hover:bg-[#f5cc3e] text-[#0a3d62] font-bold rounded-md shadow-md transition-all cursor-pointer border-0"
                  data-testid="sepa-customize-open-admin"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  <span>Personnaliser (Super Admin)</span>
                </button>
              )}
            </div>
            <div className={`grid grid-cols-1 ${nbEcheancesPreview <= 3 ? 'md:grid-cols-3' : nbEcheancesPreview === 4 ? 'md:grid-cols-4' : 'md:grid-cols-3 lg:grid-cols-6'} gap-2 text-xs`}>
              {previewDates.map((d, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded p-2 text-center">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500">Échéance {i + 1} · J+{offsetsPreview[i]}</div>
                  <div className="font-semibold text-[#0a3d62] mt-0.5">{fmtDate(d)}</div>
                  <div className="text-[#b88600] font-bold tabular-nums">{fmtEUR(previewAmounts[i])}</div>
                </div>
              ))}
            </div>
            {adminOverrides && (
              <div className="mt-3 flex items-center justify-between p-2 rounded bg-emerald-50 border border-emerald-200">
                <p className="text-[11px] text-emerald-800">
                  <span className="font-bold">Personnalisation active</span> — sera appliquée à la création du mandat.
                </p>
                <button
                  type="button"
                  onClick={() => setAdminOverrides(null)}
                  className="text-[10px] text-rose-600 hover:underline font-semibold"
                  data-testid="sepa-customize-reset"
                >
                  Réinitialiser aux défauts
                </button>
              </div>
            )}
          </div>

          {/* Sécurité */}
          <div className="flex items-start gap-2 text-xs text-slate-600 bg-emerald-50 border border-emerald-200 rounded p-3">
            <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-emerald-800">Mandat SEPA conforme</p>
              <p>Signature du mandat SEPA en ligne sur la plateforme sécurisée Stripe. Vos coordonnées bancaires ne sont jamais stockées par O'SCOP.</p>
            </div>
          </div>

          {/* CTA */}
          {!acompteVirementConfirme && (
            <div className="rounded-lg bg-amber-50 border border-amber-300 p-3 flex items-start gap-2" data-testid="sepa-acompte-waiting">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                <span className="font-bold">Étape préalable :</span> confirmez d&apos;abord le virement de l&apos;acompte {pctAcompte} % en cliquant sur
                <span className="font-bold"> « J&apos;ai effectué le virement »</span> ci-dessous. Le bouton de mise en place SEPA sera ensuite activé.
              </p>
            </div>
          )}
          <Button
            onClick={handleInitMandate}
            disabled={submitting || !montant40Cents || !acompteVirementConfirme}
            className="w-full h-12 bg-gradient-to-r from-[#DEA900] to-[#b88600] hover:from-[#f5cc3e] hover:to-[#DEA900] text-[#0a3d62] font-bold border-0 shadow-lg shadow-[#DEA900]/30 disabled:opacity-60 disabled:cursor-not-allowed disabled:grayscale active:scale-[0.99] transition-all"
            data-testid="sepa-init-btn"
            title={!acompteVirementConfirme ? `Confirmez d'abord l'acompte ${pctAcompte} % via « J'ai effectué le virement »` : undefined}
          >
            {submitting ? (
              <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Redirection sécurisée…</>
            ) : !acompteVirementConfirme ? (
              <><Lock className="h-5 w-5 mr-2" /> Mettre en place le prélèvement SEPA (acompte requis)</>
            ) : (
              <><Banknote className="h-5 w-5 mr-2" /> Mettre en place le prélèvement SEPA</>
            )}
          </Button>
        </div>
        {customOpen && typeof document !== 'undefined' && createPortal(
          <SepaPreSignCustomizeModal
            montant40Cents={montant40Cents}
            montantTotalTTC={montantTotalTTC || 0}
            splitOverride={splitOverride}
            refRCDS={refRCDS}
            initial={adminOverrides}
            onClose={() => setCustomOpen(false)}
            onApply={(vals) => {
              setAdminOverrides(vals.echeancier);
              setSplitOverride(vals.split);
              setCustomOpen(false);
              toast.success('Personnalisation enregistrée.');
            }}
          />,
          document.body
        )}
      </div>
    );
  }

  // === CAS 2 : Plan existant → affichage statut ===
  const totals = plan._totals || {};
  const mandateBadge = {
    active: { label: 'Mandat actif', cls: 'bg-emerald-600 text-white' },
    pending: { label: 'Mandat en attente', cls: 'bg-[#DEA900] text-[#0a3d62]' },
    revoked: { label: 'Mandat révoqué', cls: 'bg-rose-600 text-white' },
  }[plan.mandate_status] || { label: plan.mandate_status, cls: 'bg-slate-200 text-slate-700' };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white" data-testid="sepa-status-block">
      <div className="relative bg-gradient-to-r from-[#0a3d62] to-[#0c4a78] p-5 text-white">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")" }} />
        <div className="relative flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#DEA900] flex items-center justify-center shadow-md shadow-[#DEA900]/30 ring-1 ring-[#f5cc3e]/40 shrink-0">
              <OscopLogo size={28} variant="on-navy" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg leading-none">Plan SEPA Stripe — {pctSepa} % en {nbEcheancesPreview} prélèvements</h3>
              <p className="text-[#f5cc3e] text-[11px] uppercase tracking-wider mt-1.5">
                Mandat SEPA Stripe {(plan.mandate_id || plan.gocardless_mandate_id) ? `· ${plan.mandate_id || plan.gocardless_mandate_id}` : ''}
              </p>
            </div>
          </div>
          <Badge className={`${mandateBadge.cls} font-semibold`}>{mandateBadge.label}</Badge>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* Totaux */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total à prélever</p>
            <p className="text-xl font-bold text-[#0a3d62] tabular-nums mt-1">{fmtEUR(plan.montant_total_cents)}</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-emerald-600 font-semibold">Déjà réglé</p>
            <p className="text-xl font-bold text-emerald-700 tabular-nums mt-1">{fmtEUR(totals.paid_cents || 0)}</p>
            <p className="text-[10px] text-emerald-600 mt-0.5">{totals.paid_count || 0} / {plan.installments?.length || 3} échéances</p>
          </div>
          <div className="bg-[#DEA900]/8 border border-[#DEA900]/30 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wider text-[#b88600] font-semibold">Restant dû</p>
            <p className="text-xl font-bold text-[#b88600] tabular-nums mt-1">{fmtEUR(totals.remaining_cents || 0)}</p>
          </div>
          {totals.failed_count > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3">
              <p className="text-[10px] uppercase tracking-wider text-rose-600 font-semibold">Échouées</p>
              <p className="text-xl font-bold text-rose-700 tabular-nums mt-1">{totals.failed_count}</p>
            </div>
          )}
        </div>

        {/* Tableau des échéances */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="sepa-installments-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-bold">#</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Date</th>
                <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Montant</th>
                <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-bold">Statut</th>
              </tr>
            </thead>
            <tbody>
              {(plan.installments || []).map((inst) => {
                const badge = STATUS_BADGE[inst.status] || { label: inst.status, cls: 'bg-slate-100 text-slate-700 border-slate-200' };
                return (
                  <tr key={inst.index} className="border-b border-slate-100 last:border-b-0" data-testid={`sepa-inst-${inst.index}`}>
                    <td className="px-3 py-2 font-bold text-[#0a3d62]">{inst.index}</td>
                    <td className="px-3 py-2 text-slate-700">{fmtDate(inst.charge_date)}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-[#0a3d62]">{fmtEUR(inst.amount_cents)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 text-xs font-semibold border rounded ${badge.cls}`}>
                        {inst.status === 'paid' && <CheckCircle2 className="inline h-3 w-3 mr-1" />}
                        {inst.status === 'failed' && <AlertCircle className="inline h-3 w-3 mr-1" />}
                        {badge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {customOpen && typeof document !== 'undefined' && createPortal(
        <SepaPreSignCustomizeModal
          montant40Cents={montant40Cents}
          montantTotalTTC={montantTotalTTC || 0}
          splitOverride={splitOverride}
          refRCDS={refRCDS}
          initial={adminOverrides}
          onClose={() => setCustomOpen(false)}
          onApply={(vals) => {
            setAdminOverrides(vals.echeancier);
            setSplitOverride(vals.split);
            setCustomOpen(false);
            toast.success('Personnalisation enregistrée. Elle sera appliquée à la signature du mandat.');
          }}
        />,
        document.body
      )}
    </div>
  );
}
